import React, { useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useScenario } from '../context/ScenarioContext';
import {
  LayoutDashboard, Sliders, FileBarChart2, Wallet, Target,
  Activity, Users, BookOpenText, History, BellRing, Plug, LogOut, Menu, X,
  Sparkles, CircleDollarSign, TrendingUp, PiggyBank, ShieldCheck, Megaphone, Percent, Scale, Star,
  Gauge, Rewind, Radio, Compass, ClipboardCheck,
} from 'lucide-react';

// Sidebar navigation — grouped into 6 sections. The order mirrors how a
// user walks into the app: "How are we doing today → what's the plan →
// what actually happened → sales-specific views → who/where does the money
// go → utilities."
// All groups are always open; small uppercase headers separate them so no
// clicking is needed to reach any page.
const NAV_GROUPS = [
  {
    key: 'today',
    label: 'Today',
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
      { to: '/pulse', label: 'Daily Pulse', icon: Activity },
      { to: '/alerts', label: 'Alerts', icon: BellRing },
    ],
  },
  {
    key: 'sales',
    label: 'Sales',
    items: [
      { to: '/sales/dashboard', label: 'Sales Dashboard', icon: Gauge },
      { to: '/sales/rearview', label: 'Rearview', icon: Rewind },
      { to: '/sales/console', label: 'Console', icon: Radio },
      { to: '/sales/plan', label: 'Sales Plan', icon: Compass },
      { to: '/sales/integrity', label: 'Integrity', icon: ClipboardCheck },
    ],
  },
  {
    key: 'plan',
    label: 'Plan',
    items: [
      { to: '/plan-inputs', label: 'Plan Inputs', icon: Sliders },
      { to: '/bhag', label: 'BHAG Progress', icon: Target },
      { to: '/investments', label: 'Investment Analyzer', icon: Sparkles },
    ],
  },
  {
    key: 'report',
    label: 'Report',
    items: [
      { to: '/pnl', label: 'P&L', icon: FileBarChart2 },
      { to: '/budget-vs-actuals', label: 'Budget vs Actuals', icon: Scale },
      { to: '/cashflow', label: 'Cash Flow', icon: Wallet },
      { to: '/margin-analysis', label: 'Margin Analysis', icon: Percent },
      { to: '/marketing-roi', label: 'Marketing ROI', icon: Megaphone },
      { to: '/expense-audit', label: 'Expense Audit', icon: ShieldCheck },
    ],
  },
  {
    key: 'people',
    label: 'People & Cash',
    items: [
      { to: '/team', label: 'Team', icon: Users },
      { to: '/owners-pay', label: 'Owner\u2019s Pay', icon: CircleDollarSign },
      { to: '/profit-first', label: 'Profit First', icon: PiggyBank },
    ],
  },
  {
    key: 'system',
    label: 'System',
    items: [
      { to: '/story', label: 'Story Mode', icon: BookOpenText },
      { to: '/audit', label: 'History', icon: History },
      { to: '/integrations', label: 'Integrations', icon: Plug },
    ],
  },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  // Phase 2 · scenario picker retired. `useScenario()` still exposes the
  // Keystone via `active` so we can show its name as a chip in the header.
  const { active } = useScenario();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const sidebar = (
    <aside
      data-testid="sidebar-nav"
      className="w-72 bg-indie-navy text-indie-cream flex flex-col h-screen sticky top-0"
    >
      <div className="px-6 pt-8 pb-6 border-b border-indie-cream/10">
        <Link to="/" className="block">
          <div className="label-editorial-cream">INDIE LAW</div>
          <div className="mt-1 text-2xl font-semibold tracking-tight text-white">Command Center</div>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <div className="text-xs text-indie-cream/60">Financial OS · v1.0</div>
            {active && (
              <span
                data-testid="sidebar-keystone-chip"
                className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 bg-indie-yellow text-indie-ink uppercase tracking-wider font-semibold"
                title={`Reports read the ${active.name} plan · Keystone`}
              >
                <Star size={9} /> {active.name}
              </span>
            )}
          </div>
        </Link>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 pt-5 pb-4">
        {NAV_GROUPS.map((group, gi) => (
          <div key={group.key} className={gi > 0 ? 'mt-5' : ''}>
            <div
              className="px-3 pt-1 pb-1 text-[10px] uppercase tracking-[0.18em] text-indie-cream/40 font-semibold"
              data-testid={`nav-section-${group.key}`}
            >
              {group.label}
            </div>
            {group.items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 my-0.5 text-sm transition-colors border-l-2 ${
                      isActive
                        ? 'bg-indie-ink border-indie-yellow text-white'
                        : 'border-transparent text-indie-cream/80 hover:bg-indie-ink/60 hover:text-white'
                    }`
                  }
                  onClick={() => setMobileOpen(false)}
                >
                  <Icon size={16} strokeWidth={1.5} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>
      <div className="border-t border-indie-cream/10 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 bg-indie-yellow text-indie-ink flex items-center justify-center font-semibold text-sm">
            {user?.name?.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase() || 'IL'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white truncate">{user?.name}</div>
            <div className="text-xs text-indie-cream/60 truncate">{user?.email}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="text-indie-cream/60 hover:text-indie-yellow transition-colors"
            title="Sign out"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-indie-paper text-indie-ink">
      {/* Mobile header */}
      <div className="lg:hidden sticky top-0 z-40 bg-indie-navy text-indie-cream flex items-center justify-between px-4 py-3 border-b border-indie-yellow">
        <Link to="/" className="text-sm font-semibold tracking-widest uppercase">Indie Law · Command</Link>
        <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)} className="text-indie-cream">
          <Menu size={22} />
        </button>
      </div>
      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="relative">
            <button onClick={() => setMobileOpen(false)} className="absolute -left-10 top-4 text-white" aria-label="Close menu">
              <X size={22} />
            </button>
            {sidebar}
          </div>
        </div>
      )}
      <div className="flex">
        <div className="hidden lg:block">{sidebar}</div>
        <main className="flex-1 min-w-0">
          <div className="border-b-2 border-indie-yellow" />
          <div key={location.pathname} className="p-6 sm:p-8 lg:p-12 max-w-[1400px]">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
